/**
 * CompanySetupStepModern - Design System v2
 * Matching Login page aesthetic with glassmorphism
 * Premium glass cards with lime accents
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Search, CheckCircle, Building2, Hash, X, Sparkles } from 'lucide-react';
import { companyCreationAPI, authAPI, sessionsAPI } from '@/utils/api';
import { setAccessToken } from '@/services/tokenManager';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/utils/logger';
import { useSiretSearch, CompanyData } from '@/hooks/useSiretSearch';

interface CompanySetupStepModernProps {
  onComplete: () => void;
}

// Glass Input component
const GlassInput: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  className?: string;
}> = ({ label, value, onChange, placeholder, required, type = 'text', className = '' }) => (
  <div className={className}>
    <label className="block text-sm font-medium text-white/80 mb-2">
      {label} {required && <span className="text-lime-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className="w-full px-4 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300"
      placeholder={placeholder}
    />
  </div>
);

// Glass Button component
const GlassButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary';
  isLoading?: boolean;
  className?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, disabled, variant = 'primary', isLoading, className = '', type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || isLoading}
    className={`
      flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300
      ${variant === 'primary'
        ? 'bg-gradient-to-r from-lime-400 to-lime-500 text-teal-800 hover:shadow-lg hover:shadow-lime-500/30 hover:-translate-y-0.5 disabled:opacity-50'
        : 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 hover:border-white/30'
      }
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      ${className}
    `}
  >
    {isLoading ? (
      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
    ) : children}
  </button>
);

export const CompanySetupStepModern: React.FC<CompanySetupStepModernProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const { setUser } = useAuthStore();
  const [mode, setMode] = useState<'search' | 'edit'>('search');
  const [searchTab, setSearchTab] = useState<'siret' | 'name'>('siret');
  const [searchQuery, setSearchQuery] = useState('');
  const { searchBySiret, searchByName, clearResults, loading: searchLoading, results } = useSiretSearch();

  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    siret: '',
    address: '',
    postalCode: '',
    city: '',
    email: '',
    phone: '',
    vatNumber: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search Handler
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    if (searchTab === 'siret') {
      const result = await searchBySiret(searchQuery);
      if (result) {
        selectFromResults(result);
      }
    } else {
      await searchByName(searchQuery);
    }
  };

  // Select from search results
  const selectFromResults = (company: CompanyData) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      siret: company.siret,
      address: company.address,
      postalCode: company.postalCode,
      city: company.city,
      email: company.email || '',
      phone: company.phone || '',
      vatNumber: company.tvaNumber || '',
    });
    clearResults();
    setMode('edit');
  };

  // Create company from form data
  const handleCreateCompany = async () => {
    if (!formData.name.trim()) {
      setError(t('onboarding.companySetup.nameRequired'));
      return;
    }
    if (!formData.siret.trim()) {
      setError(t('onboarding.companySetup.siretRequired'));
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      logger.log('📤 Envoi données société:', formData);
      const response = await companyCreationAPI.create(formData);
      logger.log('📥 Réponse backend reçue:', response);

      if (response.data) {
        // ✅ Rafraîchir le token JWT pour inclure le nouveau companyId
        try {
          logger.log('🔄 Rafraîchissement du token JWT...');
          const refreshResult = await sessionsAPI.refresh();
          if (refreshResult?.accessToken) {
            // expiresIn en secondes (défaut 15 min = 900s si non fourni)
            const expiresIn = refreshResult.expiresIn || 900;
            setAccessToken(refreshResult.accessToken, expiresIn);
            logger.log('✅ Token JWT rafraîchi avec companyId');
          }
        } catch (refreshError) {
          logger.warn('⚠️ Impossible de rafraîchir le token, continuant...', refreshError);
        }

        // Mettre à jour les données utilisateur locales
        const userData = await authAPI.me();
        if (userData) {
          setUser(userData);
        }

        logger.log('✅ Société créée:', response.data);
        await new Promise(resolve => setTimeout(resolve, 500));
        logger.log('🔄 Rafraîchissement de la progression onboarding...');
        await onComplete();
        logger.log('✅ Progression rafraîchie');
      }
    } catch (err: any) {
      logger.error('❌ Erreur création société:', err);

      let errorMessage = t('onboarding.companySetup.createError');
      if (err.statusCode === 409) {
        errorMessage = err.message || t('onboarding.companySetup.siretConflict');
      } else if (err.statusCode === 400) {
        errorMessage = err.message || t('onboarding.companySetup.invalidData');
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch to manual entry mode
  const handleManualEntry = () => {
    setMode('edit');
    setSelectedCompany(null);
    setFormData({
      name: '',
      siret: '',
      address: '',
      postalCode: '',
      city: '',
      email: '',
      phone: '',
      vatNumber: '',
    });
    clearResults();
  };

  return (
    <div>
      {/* Title */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('onboarding.companySetup.title')}
        </h1>
        <p className="text-white/60">
          {t('onboarding.companySetup.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main card (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {mode === 'search' ? (
            /* MODE RECHERCHE */
            <>
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
                      <Search size={20} className="text-lime-400" />
                    </div>
                    <h2 className="text-lg font-semibold text-white">
                      {t('onboarding.companySetup.searchTitle')}
                    </h2>
                  </div>
                  <p className="text-sm text-white/60">
                    {t('onboarding.companySetup.searchSubtitle')}
                  </p>
                </div>

                {/* Tabs */}
                <div className="inline-flex rounded-2xl bg-white/10 backdrop-blur-md p-1.5 text-sm font-medium mb-6 border border-white/10">
                  <button
                    onClick={() => setSearchTab('siret')}
                    className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                      searchTab === 'siret'
                        ? 'bg-lime-500 text-teal-800 shadow-lg shadow-lime-500/30'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Hash className="w-4 h-4" />
                    {t('onboarding.companySetup.bySiret')}
                  </button>
                  <button
                    onClick={() => setSearchTab('name')}
                    className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center gap-2 ${
                      searchTab === 'name'
                        ? 'bg-lime-500 text-teal-800 shadow-lg shadow-lime-500/30'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    {t('onboarding.companySetup.byName')}
                  </button>
                </div>

                {/* Search input */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      {searchTab === 'siret' ? t('onboarding.companySetup.sirenOrSiret') : t('onboarding.companySetup.companyNameLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full px-4 py-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400/50 transition-all duration-300 pr-12"
                        placeholder={searchTab === 'siret' ? t('onboarding.companySetup.siretPlaceholderSearch') : t('onboarding.companySetup.namePlaceholderSearch')}
                      />
                      <Search className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    </div>
                    {searchTab === 'siret' && (
                      <p className="mt-2 text-xs text-white/50">
                        {t('onboarding.companySetup.siretHint')}
                      </p>
                    )}
                  </div>

                  <GlassButton
                    onClick={handleSearch}
                    disabled={searchLoading || !searchQuery.trim()}
                    isLoading={searchLoading}
                    className="w-full"
                  >
                    <Search className="w-5 h-5" />
                    {t('onboarding.companySetup.searchButton')}
                  </GlassButton>

                  <div className="flex items-center gap-2 text-xs text-white/50">
                    <div className="w-2 h-2 rounded-full bg-lime-500 animate-pulse" />
                    {t('onboarding.companySetup.officialData')}
                  </div>
                </div>
              </div>

              {/* Results list */}
              {results.length > 0 && (
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl space-y-3">
                  <h3 className="text-sm font-semibold text-white mb-4">
                    {results.length > 1 ? t('onboarding.companySetup.resultsCountPlural', { count: results.length }) : t('onboarding.companySetup.resultsCount', { count: results.length })}
                  </h3>
                  {results.map((company, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectFromResults(company)}
                      className="w-full text-left p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-lime-400/50 hover:bg-white/10 transition-all duration-300 group"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <p className="text-base font-medium text-white group-hover:text-lime-400 transition-colors">
                            {company.name}
                          </p>
                          <p className="text-sm text-white/60 mt-1">
                            SIRET: {company.siret}
                          </p>
                          <p className="text-sm text-white/50">
                            {company.address}, {company.postalCode} {company.city}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-lime-400 group-hover:translate-x-1 transition-all flex-shrink-0" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* MODE FORMULAIRE */
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-lime-500/20 flex items-center justify-center">
                    <Building2 size={20} className="text-lime-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">
                      {selectedCompany ? t('onboarding.companySetup.verifyAndComplete') : t('onboarding.companySetup.companyInfo')}
                    </h2>
                    <p className="text-sm text-white/60">
                      {selectedCompany
                        ? t('onboarding.companySetup.prefilledFromApi')
                        : t('onboarding.companySetup.fillAllInfo')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setMode('search')}
                  className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                  title={t('onboarding.companySetup.backToSearch')}
                >
                  <X className="w-5 h-5 text-white/40 hover:text-white" />
                </button>
              </div>

              {selectedCompany && (
                <div className="mb-6 px-4 py-3 bg-lime-500/10 border border-lime-400/30 rounded-2xl flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-lime-400 flex-shrink-0" />
                  <p className="text-sm text-lime-300">
                    {t('onboarding.companySetup.autoFilled')}
                  </p>
                </div>
              )}

              <div className="space-y-5">
                <GlassInput
                  label={t('onboarding.companySetup.companyNameField')}
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('onboarding.companySetup.companyNamePlaceholder')}
                  required
                />

                <GlassInput
                  label={t('onboarding.companySetup.siretField')}
                  value={formData.siret}
                  onChange={(e) => setFormData(prev => ({ ...prev, siret: e.target.value.replace(/\s/g, '') }))}
                  placeholder="123 456 789 00012"
                  required
                />

                <GlassInput
                  label={t('onboarding.companySetup.addressField')}
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Rue Example"
                />

                <div className="grid grid-cols-2 gap-4">
                  <GlassInput
                    label={t('onboarding.companySetup.postalCodeField')}
                    value={formData.postalCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, postalCode: e.target.value }))}
                    placeholder="75001"
                  />
                  <GlassInput
                    label={t('onboarding.companySetup.cityField')}
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder={t('onboarding.companySetup.cityPlaceholder')}
                  />
                </div>

                <GlassInput
                  label={t('onboarding.companySetup.vatNumberField')}
                  value={formData.vatNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                  placeholder={t('onboarding.companySetup.vatNumberPlaceholder')}
                />

                {/* Divider */}
                <div className="border-t border-white/10 pt-5">
                  <p className="text-sm font-medium text-white/70 mb-4">{t('onboarding.companySetup.contactInfo')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <GlassInput
                    label={t('onboarding.companySetup.emailField')}
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@entreprise.fr"
                  />
                  <GlassInput
                    label={t('onboarding.companySetup.phoneField')}
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="01 23 45 67 89"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-4">
          {mode === 'search' ? (
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                  <Sparkles size={16} className="text-lime-400" />
                </div>
                <h3 className="text-base font-semibold text-white">
                  {t('onboarding.companySetup.manualEntry')}
                </h3>
              </div>
              <p className="text-sm text-white/60 mb-4">
                {t('onboarding.companySetup.manualEntryDescription')}
              </p>
              <GlassButton
                onClick={handleManualEntry}
                variant="secondary"
                className="w-full"
              >
                {t('onboarding.companySetup.manualEntryButton')}
              </GlassButton>
            </div>
          ) : (
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 shadow-2xl">
              <h3 className="text-base font-semibold text-white mb-2">
                {t('onboarding.companySetup.contactInfoTitle')}
              </h3>
              <p className="text-sm text-white/60">
                {t('onboarding.companySetup.contactInfoDescription')}
              </p>
            </div>
          )}

          <div className="text-sm text-white/40 space-y-2">
            <p>{t('onboarding.companySetup.estimatedTime')}</p>
            <button className="text-lime-400 hover:text-lime-300 underline underline-offset-2 transition-colors">
              {t('onboarding.companySetup.finishLater')}
            </button>
          </div>

          {error && (
            <div className="p-4 bg-red-500/20 backdrop-blur-md border border-red-400/30 rounded-2xl text-sm text-white">
              {error}
            </div>
          )}

          {mode === 'edit' && (
            <GlassButton
              onClick={handleCreateCompany}
              disabled={isLoading || !formData.name.trim() || !formData.siret.trim()}
              isLoading={isLoading}
              className="w-full"
            >
              {t('onboarding.companySetup.continue')}
              {!isLoading && <ArrowRight className="w-5 h-5" />}
            </GlassButton>
          )}
        </div>
      </div>
    </div>
  );
};
